import React, {memo, useCallback, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import {Crosshair, MarkSeries, XYPlot} from 'react-vis';

import {urlPrefixed} from '../../config';
import type {
  HighlightSource,
  SetHighlights,
} from '../../state/interactState/highlightSource';
import {type PlotFontSize} from '../../util/plotHelpers/plotFontSize';
import {
  getPlotMargin,
  type PlotMargin,
} from '../../util/plotHelpers/plotHelpers';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {buildSingleRunLink} from '../MultiRunWorkspace/SingleRunMode/buildSingleRunLink';
import {useLinePlotSettings} from '../PanelRunsLinePlot/LinePlotSettingsContext';
import {
  getLinePlotHoverMetricContext,
  type LinePlotRenderer,
  useLinePlotHoverSession,
} from '../PanelRunsLinePlot/observability/useLinePlotHoverSession';
import {type NumericLine, type RenderLine} from './chartSeries';
import {type makeClampPoint} from './clampPoint';
import {
  FLAG_LINE_OFFSET,
  FLAG_Z_INDEX,
  useHoveredFlagPlacement,
} from './crosshairFlagPlacement';
import {
  createCrosshairPoint,
  crosshairValuesFromLines,
  getAllCrosshairPointsFromLine,
  type MarkSeriesPointForCrosshair,
} from './crosshairUtils';
import Highlight, {type BrushEndHandler} from './highlight';
import {CrosshairFlagContent} from './LinePlotCrosshairContent';
import {useHighlightValue} from './useHighlightValue';

type LinePlotCrosshairProps = {
  activeMetric?: string | symbol;
  activeRun: string | undefined;
  fontSize: PlotFontSize;
  height: number;
  hideCrosshair: boolean;
  /** The crosshair reads its own x from here, so moving it re-renders only the crosshair. */
  highlightSource: HighlightSource;
  isDrawing: boolean;
  isHovered: boolean;
  lastDrawLocation?: any;
  lines: RenderLine[];
  numericLines: NumericLine[];
  onBrushEnd: BrushEndHandler;
  onMouseDown: React.MouseEventHandler;
  onMouseUp: React.MouseEventHandler;
  /** Whether the plot is on screen; off screen the crosshair reads no highlight. */
  onScreen: boolean;
  pointClamper: ReturnType<typeof makeClampPoint>;
  renderer: LinePlotRenderer;
  runNameTruncationType?: TruncationType;
  setHighlights: SetHighlights;
  setIsDrawing: (isDrawing: boolean) => void;
  width: number;
  xAxis: string;
  xDomain: number[];
  xScale: 'linear' | 'log';
  yAxis: string;
  yDomain: number[];
  yScale: 'linear' | 'log';

  // use to override the default react-vis plot margin
  plotMargin?: PlotMargin;
};

const hardCodedTransform = {willChange: 'transform'};
const hardCodedMarkSeries = {
  fill: 'var(--wb-bg-surface)',
  strokeWidth: 2,
};

// Leftmost index where values[i].y >= y, for an array sorted ascending by y.
// Inline replacement for lodash _.sortedIndexBy to avoid a closure-per-probe and a
// search-key allocation on every mouse-move; mirrors lowerBoundCrosshairX in crosshairUtils.
// Kept specialized (not a generic lowerBound(items, target, getValue) shared with that
// helper) on purpose: a shared accessor closure would run once per probe — the per-probe
// call these inline searches exist to avoid. The near-duplicate is deliberate.
function lowerBoundByY(
  values: MarkSeriesPointForCrosshair[],
  y: number
): number {
  let lo = 0;
  let hi = values.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (values[mid].y < y) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

const LinePlotCrosshair = ({
  activeMetric,
  activeRun,
  fontSize,
  height,
  hideCrosshair,
  highlightSource,
  isDrawing,
  isHovered,
  lines,
  numericLines,
  onBrushEnd,
  onMouseDown,
  onMouseUp,
  onScreen,
  plotMargin: plotMarginOverride,
  pointClamper,
  renderer,
  runNameTruncationType,
  setHighlights,
  setIsDrawing,
  width,
  xAxis,
  xDomain,
  xScale,
  yAxis,
  yDomain,
  yScale,
}: LinePlotCrosshairProps) => {
  const {
    displayFullRunName,
    entityName,
    getNamedWorkspaceSearch,
    isMultiMetric,
    isSingleRun,
    projectName,
    singleRunNavParams,
  } = useLinePlotSettings();
  const yAxisTickTotal = yScale === 'log' ? 2 : 5;
  const crosshairWrapperRef = useRef<HTMLDivElement>(null);
  const hoverMetricContext = useMemo(
    () => getLinePlotHoverMetricContext(lines),
    [lines]
  );
  const {onMouseEnter: recordHoverStart, onMouseLeave: recordHoverEnd} =
    useLinePlotHoverSession(renderer, hoverMetricContext);

  // maximum characters we want to show in the crosshair box for hovered
  // and non-hovered tooltips
  const CROSSHAIR_MAX_LENGTH = 45;
  const CROSSHAIR_MIN_LENGTH = 8;
  const CROSSHAIR_MAX_LENGTH_NOT_HOVERED = 24;
  const getMaxStringLength = (panelWidth: number) => {
    const maxBound = CROSSHAIR_MAX_LENGTH;
    const minBound = CROSSHAIR_MIN_LENGTH;
    const maxWidth = 650; // Width where maxBound applies
    const minWidth = 300; // Width where minBound applies

    // Clamp the screen width to be within the min and max widths
    const clampedWidth = Math.max(minWidth, Math.min(panelWidth, maxWidth));
    const widthRatio = (clampedWidth - minWidth) / (maxWidth - minWidth);
    return Math.round(minBound + widthRatio * (maxBound - minBound));
  };

  const maxCharacterLength = useMemo(() => getMaxStringLength(width), [width]);

  const activeX = useHighlightValue(highlightSource, xAxis, onScreen) as
    | number
    | undefined;

  const enabledLines = useMemo(
    () => lines.filter(line => line.role === 'primary'),
    [lines]
  );
  const {highlights, crosshairCount} = useMemo(
    function createCrosshairHighlights() {
      // False line is a straight line that fits within the chart data. We render
      // it transparently and just use it for it's onNearestX callback.
      // BUT IT NO LONGER EXISTS, HA
      // so this code may be out of place now--consider refactoring
      if (enabledLines.length > 0) {
        if (enabledLines[0].kind === 'heatmap') {
          // pre-compute highlights for histogram
          const hls: any = {};
          enabledLines[0].data.forEach(row => {
            hls[row.x] = hls[row.x] || [
              {
                x: row.x,
                title: 'histogram',
                color: enabledLines[0].color,
                values: [],
              },
            ];
            hls[row.x][0].values.push(row.color);
          });
          return {
            highlights: hls,
            crosshairCount: Object.keys(hls).length,
          };
        } else {
          return {
            highlights: null,
            crosshairCount: Math.max(
              ...enabledLines.map(line => line.data.length)
            ),
          };
        }
      }
      return {highlights: null, crosshairCount: 0};
    },
    [enabledLines]
  );

  const numericAssociations = useMemo(
    () =>
      numericLines
        .filter(line => line.role === 'primary')
        .map(line => ({
          seriesId: line.seriesId,
          crosshairPoints: getAllCrosshairPointsFromLine(line),
        })),
    [numericLines]
  );
  const presentationById = new Map(
    enabledLines.map(line => [line.seriesId, line])
  );
  const enabledLinesWithCrosshairPoints = numericAssociations.map(item => ({
    line: presentationById.get(item.seriesId)!,
    crosshairPoints: item.crosshairPoints,
  }));

  const crosshairValues: MarkSeriesPointForCrosshair[] | null = useMemo(
    function calculateCrosshairValues() {
      if (activeX == null) {
        return null;
      }

      let values: MarkSeriesPointForCrosshair[];
      if (highlights == null) {
        values = crosshairValuesFromLines(
          enabledLinesWithCrosshairPoints,
          activeX,
          xDomain[0],
          xDomain[1]
        );
      } else {
        // Find closest x value
        let closest: number | null = null;
        let minDistance = Infinity;
        for (const key of Object.keys(highlights)) {
          const xVal = parseInt(key, 10);
          const distance = Math.abs(xVal - activeX);
          if (distance < minDistance) {
            minDistance = distance;
            closest = xVal;
          }
        }
        if (closest == null) {
          return null;
        }

        values = highlights[closest];
      }

      // Always include baseline run if it exists, even if it doesn't have data at cursor
      const baselineLine = enabledLinesWithCrosshairPoints.find(
        l => l.line.isBaseline
      );
      if (!baselineLine) {
        return values;
      }
      if (values.some(v => v.isBaseline)) {
        return values;
      }
      if (baselineLine.crosshairPoints.length === 0) {
        // Skip if no crosshair points available
        return values;
      }

      // Baseline not in values, use first point if cursor before baseline, last point if cursor after
      const firstPoint = baselineLine.crosshairPoints[0];
      const lastPoint = baselineLine.crosshairPoints.at(-1);

      const useFirstPoint = firstPoint && firstPoint.point.x > activeX;
      const pointToShow = useFirstPoint ? firstPoint : lastPoint;
      const pointIndex = useFirstPoint
        ? 0
        : baselineLine.crosshairPoints.length - 1;

      if (pointToShow) {
        const prevPoint =
          pointIndex > 0
            ? baselineLine.crosshairPoints[pointIndex - 1]
            : undefined;

        const baselinePoint = createCrosshairPoint(baselineLine.line, {
          point: pointToShow.point,
          minmaxPoint: pointToShow.minmaxPoint,
          stddevPoint: pointToShow.stddevPoint,
          prevPoint: prevPoint?.point ?? null,
        });
        values = [baselinePoint, ...values];
      }

      return values;
    },
    [enabledLinesWithCrosshairPoints, activeX, highlights, xDomain]
  );

  const showMarkSeries =
    !hideCrosshair &&
    crosshairValues != null &&
    crosshairValues.length > 0 &&
    crosshairValues[0].values == null;

  const handleMouseMoveWithXY = useCallback(
    (mouseX: number, mouseY: number) => {
      const newHighlights: Array<{
        axis: string;
        metric: string | undefined;
        value: string | number | undefined;
      }> = [{axis: xAxis, metric: undefined, value: mouseX}];

      // Compute the next crosshair values now, and then find the
      // the line whose crosshair point y-value is nearest to
      // mouseY. This is a little strange but it mostly works.
      // Sort a copy: crosshairValuesFromLines hands the same array to the
      // render below when the arguments repeat, and that path depends on the
      // original line order.
      const newCrosshairValues = crosshairValuesFromLines(
        enabledLinesWithCrosshairPoints,
        mouseX,
        xDomain[0],
        xDomain[1]
      )
        .slice()
        .sort((a, b) => (a.y as number) - (b.y as number));
      if (newCrosshairValues.length > 0) {
        let lineIndex = lowerBoundByY(newCrosshairValues, mouseY);
        if (lineIndex === newCrosshairValues.length) {
          lineIndex = lineIndex - 1;
        } else if (lineIndex !== 0 && newCrosshairValues.length > 1) {
          lineIndex =
            newCrosshairValues[lineIndex].y - mouseY <
            mouseY - newCrosshairValues[lineIndex - 1].y
              ? lineIndex
              : lineIndex - 1;
        }

        newHighlights.push({
          axis: 'run:name',
          metric: newCrosshairValues[lineIndex].metricName,
          value: newCrosshairValues[lineIndex].uniqueId,
        });
      }
      setHighlights(newHighlights);
    },
    [enabledLinesWithCrosshairPoints, setHighlights, xAxis, xDomain]
  );

  const handleMouseLeave = useCallback(() => {
    recordHoverEnd();
    setHighlights([
      {axis: xAxis, value: undefined},
      {axis: 'run:name', value: undefined, metric: undefined},
    ]);
    // setHighlightRun(null);
  }, [recordHoverEnd, setHighlights, xAxis]);

  const markSeriesValues = useMemo(
    function markSeriesValuesMemo() {
      return crosshairValues
        ?.filter(v => {
          const runMatch = v.uniqueId === activeRun;
          if (isMultiMetric && activeMetric != null) {
            return runMatch && v.metricName === activeMetric;
          }
          return runMatch;
        })
        .map(p => pointClamper(p));
    },
    [crosshairValues, activeRun, activeMetric, isMultiMetric, pointClamper]
  );

  const reducedCrosshairValues =
    activeX != null && crosshairValues && crosshairValues.length > 0
      ? [
          crosshairValues.reduce((closest, pt) =>
            Math.abs((pt.x as number) - activeX!) <
            Math.abs((closest.x as number) - activeX!)
              ? pt
              : closest
          ),
        ]
      : undefined;

  const plotMargin = useMemo(
    function plotMarginMemo() {
      if (plotMarginOverride) {
        return plotMarginOverride;
      }
      const margin = getPlotMargin({
        axisKeys: {xAxis, yAxis},
        axisDomain: {
          yAxis: yDomain,
        },
        axisType: {yAxis: yScale},
        tickTotal: {yAxis: yAxisTickTotal},
        fontSize,
      });
      return margin;
    },
    [
      fontSize,
      xAxis,
      yAxis,
      yAxisTickTotal,
      yDomain,
      yScale,
      plotMarginOverride,
    ]
  );

  // Calculate the x pixel position for the custom crosshair line
  // We render our own line so it appears behind the MarkSeries circles
  let crosshairLineX = null;
  if (reducedCrosshairValues && reducedCrosshairValues.length > 0) {
    const xValue = reducedCrosshairValues[0].x;
    const innerWidth = width - (plotMargin.left ?? 0) - (plotMargin.right ?? 0);

    let xRatio: number;
    if (xScale === 'log') {
      xRatio =
        (Math.log(xValue) - Math.log(xDomain[0])) /
        (Math.log(xDomain[1]) - Math.log(xDomain[0]));
    } else {
      xRatio = (xValue - xDomain[0]) / (xDomain[1] - xDomain[0]);
    }

    crosshairLineX = xRatio * innerWidth;
  }

  const innerHeight = height - (plotMargin.top ?? 0) - (plotMargin.bottom ?? 0);

  const flagPlacement = useHoveredFlagPlacement({
    wrapperRef: crosshairWrapperRef,
    isHovered,
    plotMargin,
    crosshairLineX,
  });

  const highlightedLine = useMemo(() => {
    if (!activeRun) {
      return undefined;
    }
    return lines.find(l => {
      const runMatch = l.highlightId === activeRun;
      if (isMultiMetric && activeMetric != null) {
        return runMatch && l.metricName === activeMetric;
      }
      return runMatch;
    });
  }, [lines, activeRun, activeMetric, isMultiMetric]);

  const isHighlightedLineGrouped =
    highlightedLine != null && highlightedLine.isGrouped;
  const isCmdClickAllowed = !isHighlightedLineGrouped && !isSingleRun;
  const activeRunLink =
    activeRun != null
      ? buildSingleRunLink(
          singleRunNavParams,
          {
            entityName: highlightedLine?.entityName ?? entityName,
            projectName: highlightedLine?.projectName ?? projectName,
            runName: activeRun,
          },
          getNamedWorkspaceSearch()
        )
      : '';

  /**
   * Special navigation handling to route users who want to navigate to a run directly by click while holding down the meta key
   */
  const handlePlotClick = useCallback(
    (e: React.MouseEvent) => {
      if (!e.metaKey || !isCmdClickAllowed) {
        return;
      }
      window.open(urlPrefixed(activeRunLink), '_blank', 'noopener,noreferrer');
    },
    [isCmdClickAllowed, activeRunLink]
  );

  const canRender = highlightedLine != null;

  return (
    <div
      ref={crosshairWrapperRef}
      data-id="crosshair-wrapper"
      key="line-plot-crosshair-wrapper"
      className="hide-rv-crosshair-line"
      style={{overflow: 'hidden', position: 'relative'}}>
      {/* Custom crosshair line rendered before XYPlot so SVG content (MarkSeries) appears on top */}
      {!hideCrosshair && crosshairLineX != null && (
        <div
          className="custom-crosshair-line"
          style={{
            position: 'absolute',
            left: (plotMargin.left ?? 0) + crosshairLineX,
            top: plotMargin.top ?? 0,
            width: 1,
            height: innerHeight,
            background: '#47d3d9', // same as react-vis https://github.com/uber/react-vis/blob/master/packages/react-vis/src/styles/plot.scss#L77-L80
            pointerEvents: 'none',
          }}
        />
      )}
      <XYPlot
        animation={false}
        dontCheckIfEmpty
        height={height}
        key="line-plot-crosshair"
        margin={plotMargin}
        onMouseDown={onMouseDown}
        onMouseEnter={recordHoverStart}
        onMouseUp={handlePlotClick}
        onMouseLeave={handleMouseLeave}
        // willChange: transform forces the crosshair into it's own rendering layer which means the plot behind won't be re-rendered when the crosshair moves. This improved FPS from 2 to >60 on large plots.
        style={hardCodedTransform}
        width={width}
        xDomain={xDomain}
        xType={xScale}
        yDomain={yDomain}
        yType={yScale}>
        <Highlight
          key="lineplot-crosshair-highlight"
          stepCount={crosshairCount}
          onMouseUp={onMouseUp}
          onBrushEnd={onBrushEnd}
          onMouseMoveWithXY={handleMouseMoveWithXY}
          isDrawing={isDrawing}
          setIsDrawing={setIsDrawing}
        />

        {showMarkSeries && (
          <MarkSeries
            colorType="literal"
            data={markSeriesValues}
            key="cross-hair-literal-mark-series"
            size={4}
            style={hardCodedMarkSeries}
          />
        )}

        {!hideCrosshair && crosshairValues && crosshairValues.length > 0 && (
          <Crosshair values={reducedCrosshairValues}>
            {/* crosshairFlagContent is duplicated as part of a hack to
              prevent the flag from getting cut off by the panel boundaries
              the div with name line-plot-flag-escaping has display: none overridden elsewhere
              only for the highlighted panel. For the other panels this div doesn't appear. */}

            <div className="line-plot-flag">
              {/* Skipped while this panel is hovered: the panel's :hover rules
                  set `visibility: hidden` on .line-plot-flag, so this copy is
                  invisible and the portalled copy below is what the user sees.
                  Nothing measures this copy — react-vis places the crosshair box
                  from the cursor's x position, and the portalled copy is placed
                  from the wrapper's bounding rect (see useHoveredFlagPlacement).
                  Rendering it anyway would build the whole tooltip twice on
                  every crosshair move. */}
              {canRender && !isHovered && (
                <CrosshairFlagContent
                  allowCmdClickRun={false}
                  allowCopyContents={
                    false /* don't want the truncated copy to generate data*/
                  }
                  crosshairValues={crosshairValues}
                  highlightMetric={activeMetric}
                  highlightRun={activeRun}
                  isHovered={isHovered}
                  isSparkLines={
                    enabledLines.length > 0 &&
                    enabledLines[0].kind === 'heatmap'
                  }
                  maxLength={
                    isHovered
                      ? maxCharacterLength
                      : Math.min(
                          CROSSHAIR_MAX_LENGTH_NOT_HOVERED,
                          maxCharacterLength
                        )
                  }
                  truncateLegend={
                    displayFullRunName && isHovered ? false : true
                  }
                  runNameTruncationType={runNameTruncationType}
                  xAxis={xAxis}
                />
              )}
            </div>
            {isHovered &&
              createPortal(
                <div
                  className="line-plot-flag-escaping tw-style"
                  data-test="active-flag-content"
                  style={{
                    position: 'fixed',
                    left: flagPlacement.left,
                    top: flagPlacement.top,
                    transform: flagPlacement.placeLeftOfLine
                      ? `translateX(calc(-100% - ${FLAG_LINE_OFFSET}px))`
                      : `translateX(${FLAG_LINE_OFFSET}px)`,
                    maxWidth: flagPlacement.maxWidth,
                    overflow: 'hidden',
                    zIndex: FLAG_Z_INDEX,
                    pointerEvents: 'none',
                  }}>
                  <CrosshairFlagContent
                    allowCmdClickRun={isCmdClickAllowed}
                    allowCopyContents={isHovered}
                    crosshairValues={crosshairValues}
                    highlightMetric={activeMetric}
                    highlightRun={activeRun}
                    isHovered={isHovered}
                    isSparkLines={
                      enabledLines.length > 0 &&
                      enabledLines[0].kind === 'heatmap'
                    }
                    maxLength={maxCharacterLength}
                    truncateLegend={
                      displayFullRunName && isHovered ? false : true
                    }
                    runNameTruncationType={runNameTruncationType}
                    xAxis={xAxis}
                  />
                </div>,
                document.body
              )}
          </Crosshair>
        )}
      </XYPlot>
    </div>
  );
};

export default memo(LinePlotCrosshair);
